/**
 * Change request detail page — view of a single proposed mutation.
 *
 * Two audiences reach this page since Feature 2 (My Change Requests) shipped:
 *   - Compliance / Admin (Checkers) — full decision controls: Approve / Reject
 *     / Cancel, plus decision-notes textarea.
 *   - Makers — read-only by default; they can Cancel their OWN pending CR but
 *     do NOT see Approve / Reject (those are server-gated 403 anyway, but
 *     hiding them removes the confusing "Could not approve / Compliance or
 *     Admin role required" alert path).
 *
 * Back-link target also depends on audience: Checkers return to /change-requests
 * (the Compliance queue), Makers return to /my-change-requests.
 *
 * URL: /change-requests/:id
 */
import {
  Alert, Badge, Button, Card, Code, Container, Group, Stack, Table, Text,
  Textarea, Title,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconArrowLeft, IconBan, IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlertModal } from '../components/AlertModalProvider';
import { EntityFriendlyLabel } from '../components/EntityFriendlyLabel';
import { PayloadDiffTable } from '../components/PayloadDiffTable';
import { DocumentUploadSummary } from '../components/DocumentUploadSummary';
import { CascadePlanPreviewCard } from '../components/CascadePlanPreviewCard';
import { PolicyPlanPreviewCard } from '../components/PolicyPlanPreviewCard';
import type { PolicyPlan } from '../components/PolicyPlanPreviewCard';
import { HighImpactApprovalGate } from '../components/HighImpactApprovalGate';
import { StatusExplained } from '../components/StatusExplained';
import type { CascadePlan } from '../api/agreements';
import {
  useApproveChangeRequest, useCancelChangeRequest,
  useChangeRequest, useRejectChangeRequest,
} from '../hooks/useChangeRequests';
import { useCurrentUser } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { CHANGE_REQUEST_KIND_LABELS, CHANGE_REQUEST_STATUS_LABELS } from '../api/types';

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'yellow',
  APPLIED: 'green',
  REJECTED: 'red',
  CANCELLED: 'gray',
};

export function ChangeRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: cr, isLoading, error } = useChangeRequest(id);
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const approve = useApproveChangeRequest();
  const reject = useRejectChangeRequest();
  const cancel = useCancelChangeRequest();
  const { showAlert } = useAlertModal();
  const [notes, setNotes] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  useDocumentTitle('Change request');

  if (isLoading) return <Container size="xl"><Text c="dimmed">Loading…</Text></Container>;
  if (error || !cr) {
    return (
      <Container size="xl">
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          Change request not found.
        </Alert>
      </Container>
    );
  }

  // #14 P2: "checker" is now the cr.approve permission (seeded admin+compliance,
  // so behaviour-preserving) rather than a hard-coded role test.
  const isChecker = can('cr.approve');
  const isRequester = !!user && cr.requested_by === user.id;
  const isPending = cr.status === 'PENDING';
  // Approve / Reject are Checker-only. Cancel is allowed to the requester
  // (their own pending CR) OR to a Checker. Hiding these buttons for non-
  // entitled viewers removes the confusing 403 path Pedro hit when a Maker
  // arrived from the dashboard "Pending Change Requests" card via My CR.
  const canApproveOrReject = isPending && isChecker;
  const canCancel = isPending && (isChecker || isRequester);
  const showDecisionCard = canApproveOrReject || canCancel;
  // Back-link target depends on which queue the user came from.
  const backTo = isChecker ? '/change-requests' : '/my-change-requests';

  function fire(action: 'approve' | 'reject' | 'cancel') {
    const fn = action === 'approve' ? approve : action === 'reject' ? reject : cancel;
    fn.mutate(
      { id: cr!.id, input: { decision_notes: notes || undefined } },
      {
        onSuccess: () => {
          showAlert({
            color: action === 'approve' ? 'blue' : 'orange',
            title: `Request ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'cancelled'}`,
            message: action === 'approve'
              ? 'The change has been applied to the live record.'
              : `The request was ${action === 'reject' ? 'rejected' : 'cancelled'}; the live record is unchanged.`,
          });
          setNotes('');
        },
        onError: (e) => {
          showAlert({ color: 'red', title: `Could not ${action}`, message: e.detail });
        },
      },
    );
  }

  // Phase 5 (C-160): compute the plan preview ONCE so the same card renders
  // both in the page flow and inside the high-impact approve gate.
  const payloadObj = cr.payload as Record<string, unknown> | null;
  let planPreview: ReactNode = null;
  if (cr.entity_type === 'agreements' && cr.kind === 'UPDATE' && payloadObj?._cascade_plan) {
    planPreview = <CascadePlanPreviewCard plan={payloadObj._cascade_plan as CascadePlan} />;
  } else if (cr.entity_type === 'insurance_policies' && cr.kind === 'UPDATE') {
    if (payloadObj?._detach_plan) {
      planPreview = <PolicyPlanPreviewCard flavour="detach" plan={payloadObj._detach_plan as PolicyPlan} />;
    } else if (payloadObj?._terminate_plan) {
      planPreview = <PolicyPlanPreviewCard flavour="terminate" plan={payloadObj._terminate_plan as PolicyPlan} />;
    } else if (payloadObj?._move_plan) {
      planPreview = <PolicyPlanPreviewCard flavour="move" plan={payloadObj._move_plan as PolicyPlan} />;
    }
  }
  const highImpact = isPending && Boolean(cr.impact?.is_high_impact);

  return (
    <Container size="xl">
      <Stack>
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(backTo)}>Back</Button>
          <Title order={2}>Change request</Title>
          <StatusExplained entity="change_request" status={cr.status}>
            <Badge size="lg" color={STATUS_COLOR[cr.status]}>{CHANGE_REQUEST_STATUS_LABELS[cr.status] ?? cr.status}</Badge>
          </StatusExplained>
          <Badge size="lg" variant="light">{CHANGE_REQUEST_KIND_LABELS[cr.kind] ?? cr.kind}</Badge>
        </Group>

        {/* Phase 5 (C-160): loud checker-side high-impact warning, shown to
            every viewer of a pending high-impact CR (symmetric with the
            maker-side cascade modal at filing time). */}
        {highImpact && cr.impact && (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}
            title="High-impact change">
            {cr.impact.affected_total > 0
              ? <>Approving this will cascade to <strong>{cr.impact.affected_total} linked record{cr.impact.affected_total === 1 ? '' : 's'}</strong>. Review the change below, then approve.</>
              : <>Approving this allocates a permanent client number (Promote to Active). Review the change below, then approve.</>}
          </Alert>
        )}

        <Card withBorder p="md" radius="sm">
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th w={180}>Entity type</Table.Th>
                <Table.Td><Code>{cr.entity_type}</Code></Table.Td>
              </Table.Tr>
              <Table.Tr>
                {/* Beta #12 (2026-05-26): friendly identifier as primary
                    display, raw UUID demoted to a small subtitle so
                    Compliance can still copy-paste it when needed. */}
                <Table.Th>Record</Table.Th>
                <Table.Td>
                  {/* #49 (2026-06-13, C-134): prefer the server-resolved
                      label (resolves even soft-deleted DELETE targets);
                      `?? undefined` falls back to the legacy client-side
                      path when the server returned null. */}
                  <EntityFriendlyLabel
                    entityType={cr.entity_type}
                    entityId={cr.entity_id}
                    preResolvedLabel={cr.entity_friendly_label ?? undefined}
                  />
                  {cr.entity_id && (
                    <div style={{ marginTop: 4 }}>
                      <Code style={{ fontSize: 11 }}>{cr.entity_id}</Code>
                    </div>
                  )}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Requested by</Table.Th>
                <Table.Td>{cr.requested_by_name ?? cr.requested_by}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Requested at</Table.Th>
                <Table.Td>{new Date(cr.requested_at).toLocaleString()}</Table.Td>
              </Table.Tr>
              {cr.decided_by && (
                <Table.Tr>
                  <Table.Th>Decided by</Table.Th>
                  <Table.Td>{cr.decided_by_name ?? cr.decided_by}</Table.Td>
                </Table.Tr>
              )}
              {cr.decided_at && (
                <Table.Tr>
                  <Table.Th>Decided at</Table.Th>
                  <Table.Td>{new Date(cr.decided_at).toLocaleString()}</Table.Td>
                </Table.Tr>
              )}
              {cr.justification && (
                <Table.Tr>
                  <Table.Th>Maker's justification</Table.Th>
                  <Table.Td>{cr.justification}</Table.Td>
                </Table.Tr>
              )}
              {cr.decision_notes && (
                <Table.Tr>
                  <Table.Th>Decision notes</Table.Th>
                  <Table.Td>{cr.decision_notes}</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Card>

        <Card withBorder p="md" radius="sm">
          <Text fw={600} size="sm" mb="xs">
            Proposed change
          </Text>
          {/* Beta #1 (2026-05-26): Field / Old / New table replaces the
              JSONB dump so Compliance can read a CR at a glance.
              P23 (2026-10-10): a document upload reads in words, with the file
              to open before deciding. */}
          {cr.entity_type === 'documents' && cr.kind === 'CREATE'
            ? <DocumentUploadSummary cr={cr} canOpenPending={isChecker || isRequester} />
            : <PayloadDiffTable cr={cr} />}
        </Card>

        {/* #262 / #26: cascade + policy-lifecycle plan previews for the
            approver. Computed once above (planPreview) so the very same card
            also renders inside the high-impact approve gate. */}
        {planPreview}

        {showDecisionCard && (
          <Card withBorder p="md" radius="sm">
            <Stack>
              <Text fw={600} size="sm">
                {canApproveOrReject ? 'Decision' : 'Withdraw this request'}
              </Text>
              <Textarea
                label={canApproveOrReject
                  ? 'Decision notes (optional for approve, recommended for reject/cancel)'
                  : 'Reason for cancelling (optional)'}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                autosize minRows={2}
              />
              <Group>
                {canApproveOrReject && (
                  <>
                    <Button color="green" leftSection={<IconCheck size={14} />}
                      loading={approve.isPending}
                      onClick={() => { if (highImpact) setGateOpen(true); else fire('approve'); }}>
                      Approve & apply
                    </Button>
                    <Button color="red" variant="light" leftSection={<IconX size={14} />}
                      loading={reject.isPending}
                      onClick={() => fire('reject')}>Reject</Button>
                  </>
                )}
                {canCancel && (
                  <Button color="gray" variant="subtle" leftSection={<IconBan size={14} />}
                    loading={cancel.isPending}
                    onClick={() => fire('cancel')}>Cancel</Button>
                )}
              </Group>
              {!canApproveOrReject && canCancel && (
                <Text size="xs" c="dimmed">
                  This is your own pending request. You can withdraw it, but only
                  Compliance / Admin can approve or reject.
                </Text>
              )}
            </Stack>
          </Card>
        )}

        {/* Phase 5 (C-160): high-impact approve gate. Reject/Cancel never
            open it — only Approve & apply does, and only when high-impact. */}
        {cr.impact && (
          <HighImpactApprovalGate
            opened={gateOpen}
            impact={cr.impact}
            loading={approve.isPending}
            onCancel={() => setGateOpen(false)}
            onConfirm={() => { setGateOpen(false); fire('approve'); }}
          >
            {planPreview}
          </HighImpactApprovalGate>
        )}
      </Stack>
    </Container>
  );
}
